import { Field, FieldProps, Formik, FormikProps } from 'formik';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import useInitialErrors from '../../../hooks/formik-initial-errors';
import { object as objectYup, string as stringYup, ref as refYup } from 'yup';
import { userOtpService } from '../../../lib/api/userOtpValidate';
import getValue from 'lodash/get';
import { emailRegexp, passwordRegExp } from '../../../lib/utils/validation';
import { OTPType, OtpValidationType } from '../../../types/userOtp';
import Swal from 'sweetalert2';
import PhoneCodeDropdown from '../../../components/dropdown/phoneCode';
import { sessionStorageGetItem, sessionStorageSetItem } from '../../../components/storage/sessionStorageManager';
import { localStorageGetItem, localStorageSetItem } from '../../../components/storage/localStorageManager';
import { Message, SweetAlertTitle, WarningIcon } from '../../../lib/constants/messages';
import { history } from '../../../history';
import { KYCdigioSign } from '../../../lib/utils/digio-kyc-sign';
import { isKYCDigioSignFeatureEnabled } from '../../../lib/utils/common-functions';
import { useScript } from '../../../hooks/script';
import { useKYCDigioSignFlow } from '../../../hooks/useSelector';
import { digioKYCUrl } from '../../../config/constants';
import { getKYCRequestIdAndToken } from '../../../lib/api/digioKYC';
import { getKYCDigioSignSettings } from '../../../redux/KYCDigioSign';
import { claimedCandidateExternalService } from '../../../lib/api/claimed';
import { decryptData } from '../../claimed/search-box';
import { routes } from '../../routes/routes-names';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../../redux/reduxhooks';
import { useNavigate } from 'react-router-dom';
import { useRegistration } from '../../../components/registration/RegistrationContext';

type Props = {
    handleSelect?: (key: string, key2: string, value: any) => void;
    setShowSelectAccountType?: (key: boolean) => void;
    candidateType?: string;
    disableButton?: boolean;
    showPasswordTab?: boolean;
    showOTPBtn?: boolean;
    showNextTab?: boolean;
    setShowNextTab?: (key: boolean) => void;
    setShowOTPBtn?: (key: boolean) => void;
    setPageLoading?: (key: boolean) => void;
    setDigioSuccessData?: (key: any) => void;
    setShowCustomSinUpTab?: (key: boolean) => void;
    setShowPasswordTab?: (key: boolean) => void;
    claimedCandidateData?: any;
    isNewRegUnclaimedAcc?: boolean;
    newRegUnclaimedId?: string;
    activeTab?: string;
};

const initialFormData = {
    firstName: '',
    lastName: '',
    OwnerPhoneNumber: '',
    OwnerCountryCode: '',
    email: '',
    verificationCode: '',
    otp: '',
    password: '',
    cpassword: '',
};

const CustomAuth: React.FC<Props> = () => {
    const [, setIsOTPNotValidated] = useState(false);
    const [, setRemoveSentMessage] = useState(false);
    const [, setEnableGetOtpBtn] = useState(true);
    const [, setOtpExpired] = useState(false);

    const [otpVerified] = useState(false);
    const [base64PassString, setBase64PassString] = useState('');
    const [loadingSpin, setLoadingSpin] = useState(false);
    const [showCpass, setShowCpass] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [userPhoneCodeError, setUserPhoneCodeError] = useState('');
    const [showInvalidClaimedEmail, setShowInvalidClaimedEmail] = useState(false);
    const [newRegCandidateEmail, setNewRegCandidateEmail] = useState('');
    const [sessionData, setSessionData] = useState<any>();
    const [country, setCountry] = useState("")
    const [isLoadingUnclaimedData, setIsLoadingUnclaimedData] = useState(false);

    const {
        activeTab,
        candidateType,
        setDigioSuccessData,
        setPageLoading,
        setShowOTPBtn,
        setShowPasswordTab,
        setShowSelectAccountType,
        showNextTab,
        showOTPBtn,
        showPasswordTab,
        ConfirmUnclaimedCandidateInvitationId,
        unclaimedData,
        setShowCustomSinUpTab,
        handleSelect,
    } = useRegistration();

    const [isNewRegUnclaimedAcc] = useState(ConfirmUnclaimedCandidateInvitationId ? true : false);

    const [newRegUnclaimedId] = useState(ConfirmUnclaimedCandidateInvitationId?.toString() || '');
    const [claimedCandidateData] = useState(unclaimedData);

    const digioKYCReady = useScript(digioKYCUrl);
    const kycDigioSignFlowSettings = useKYCDigioSignFlow();
    const customEmail = localStorageGetItem('Email');
    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const affilateReferral = localStorageGetItem('referral', true);

    const profileFormData = useMemo(() => {
        if (claimedCandidateData || newRegCandidateEmail || sessionData) {
            return {
                firstName: sessionData?.FirstName || claimedCandidateData?.FirstName || '',
                lastName: sessionData?.LastName || claimedCandidateData?.LastName || '',
                OwnerPhoneNumber: sessionData?.OwnerPhoneNumber || claimedCandidateData?.Phone || '',
                email: newRegCandidateEmail || '',
                password: '',
                cpassword: '',
            };
        }
        return initialFormData;
    }, [claimedCandidateData, newRegCandidateEmail, sessionData]);

    const initialErrors = useInitialErrors(
        profileFormData,
        getValidationSchema(candidateType, ConfirmUnclaimedCandidateInvitationId)
    );

    const handleKYCSuccess = (response: any) => {
        setDigioSuccessData?.(response);
        setTimeout(() => {
            handleSelect('basic-details', candidateType, {});
            setShowSelectAccountType(false);
            setPageLoading?.(false);
        }, 1000);
    };

    const handleKYCFailure = (response: any) => {
        setPageLoading?.(false);
    };

    const fetchKYCRequestToken = async () => {
        const { requestId, accessToken } = await getKYCRequestIdAndToken(customEmail);
        if (requestId && accessToken) {
            KYCdigioSign(requestId, customEmail, accessToken, handleKYCSuccess, handleKYCFailure);

            localStorageSetItem('requestId', requestId);
        }
    };

    const submitForm = useCallback(
        async (values: any) => {
            try {
                setPageLoading?.(true);
                if (
                    candidateType === 'candidate' &&
                    isKYCDigioSignFeatureEnabled(kycDigioSignFlowSettings) &&
                    digioKYCReady &&
                    customEmail
                ) {
                    fetchKYCRequestToken();
                } else {
                    handleSelect('signup-form', candidateType, values);
                    setShowSelectAccountType(false);
                    setPageLoading?.(false);
                }

                // sessionStorageSetItem(
                //     'user-basic-details',
                //     {
                //         FirstName: values.firstName,
                //         LastName: values.lastName,
                //         OwnerPhoneNumber: values.OwnerPhoneNumber,
                //         OwnerCountryCode: values.OwnerCountryCode,
                //         UserCountryCode: values.OwnerCountryCode,
                //         Email: customEmail || values.email,
                //     },
                //     false
                // );
            } catch (error) {
                setPageLoading?.(false);
            }
        },
        [customEmail, digioKYCReady, kycDigioSignFlowSettings, setPageLoading, setShowSelectAccountType]
    );

    const fetchData = async (
        emailName: string,
        otpType: OTPType,
        phone: string,
        countryCode: string,
        setFieldValue?: any
    ) => {
        try {
            setLoadingSpin(true);

            const path = history.location.pathname;

            const {
                resultObject: { otpValidateType, email, isEmailInvalid },
            } =
                path !== '/api-integrate'
                    ? await userOtpService.updateUserOtp({
                        email: emailName,
                        otpType: affilateReferral ? OTPType.AffiliateMarketing : otpType,
                        phone,
                        countryCode,
                    })
                    : await userOtpService.updateHRMSUserOtp({
                        email: emailName,
                        otpType: affilateReferral ? OTPType.AffiliateMarketing : otpType,
                        phone,
                        countryCode,
                    });

            if (claimedCandidateData?.IsUser === 1 && isEmailInvalid) {
                setFieldValue('email', email);
                setShowInvalidClaimedEmail(true);
            } else {
                setShowInvalidClaimedEmail(false);
                if (otpValidateType === OtpValidationType.Valid) {
                    setRemoveSentMessage(true);
                    setShowOTPBtn?.(false);
                    setIsOTPNotValidated(false);
                    setLoadingSpin(false);
                } else if (otpValidateType === OtpValidationType.InValid) {
                    setLoadingSpin(false);
                    Swal.fire({
                        title: SweetAlertTitle.Sorry,
                        icon: WarningIcon,
                        text: Message.EmailAlreadyRegistered,
                    });
                }
            }
            setLoadingSpin(false);
        } catch (err) {
            setLoadingSpin(false);
        }
    };

    
    useEffect(() => {
        if (newRegUnclaimedId) {
              setIsLoadingUnclaimedData(true);
            (async () => {
                try {
                    const { resultObject } = await claimedCandidateExternalService.getUnClaimedCandiateEmail(
                    newRegUnclaimedId
                );

                if (resultObject.hash !== 'NA') {
                    const decryptedHashedData = decryptData(resultObject.hash, false);
                    setNewRegCandidateEmail(decryptedHashedData.Email || '');
                    setSessionData({ ...decryptedHashedData, OwnerPhoneNumber: decryptedHashedData.Phone })
                    localStorageSetItem('Email', decryptedHashedData.Email);
                    setCountry(decryptedHashedData?.country)
                } else {
                    toast.error('You have already registered via this link.');
                    navigate(routes.main);
                }
                } catch (error) {
                    toast.error('Failed to fetch unclaimed candidate data.');
                    console.error(error)
                }
                finally{
                    setIsLoadingUnclaimedData(false)
                }
            })();
        }
    }, [kycDigioSignFlowSettings, newRegUnclaimedId]);

    useEffect(() => {
        if (otpVerified) {
            setIsOTPNotValidated(false);
            setRemoveSentMessage(false);
            setOtpExpired(false);
        }
    }, [otpVerified]);

    const convertToBase64 = (value: string) => {
        const encodedString = btoa(value);
        setBase64PassString(encodedString);
    };

    useEffect(() => {
        localStorageSetItem('Password', base64PassString);
    }, [base64PassString]);


    useEffect(() => {
        if (isKYCDigioSignFeatureEnabled(kycDigioSignFlowSettings)) {
            if (!kycDigioSignFlowSettings?.settings) dispatch(getKYCDigioSignSettings());
        }

        if (activeTab === '/signup' && showNextTab) {
            if (candidateType === 'company') {
                setShowPasswordTab?.(true);
            }
        } else {
            if (candidateType === 'candidate') {
                setShowPasswordTab?.(true);
            } else {
                setShowPasswordTab?.(false);
            }
        }

        const storageData = sessionStorageGetItem('user-basic-details', false)!;

        if (storageData) {
            setSessionData({ ...storageData });
        }
    }, []);

    return (
        
        <div>
           { isLoadingUnclaimedData ? <> <div className="card lt-shadow-sm mb-4">
    <div className="login-card-body">
        <div className="container-fluid">
            <div className="row mb-4">
                <div className="col-md-12 text-center">
                    <div className="placeholder-wave">
                        <h1 className="placeholder col-8 mx-auto mb-3" style={{height: '32px'}}></h1>
                        <div className="placeholder col-6 mx-auto" style={{height: '20px'}}></div>
                    </div>
                </div>
            </div>
            <div className="row mb-5">
                <div className="col-md-6">
                    <div className="mb-4">
                        <div className="placeholder-wave">
                            <div className="placeholder col-4 mb-2" style={{height: '20px'}}></div>
                            <div className="placeholder col-12" style={{height: '44px', borderRadius: '4px'}}></div>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-4">
                        <div className="placeholder-wave">
                            <div className="placeholder col-4 mb-2" style={{height: '20px'}}></div>
                            <div className="placeholder col-12" style={{height: '44px', borderRadius: '4px'}}></div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-12 col-md-12">
                    <div className="mb-4">
                        <div className="placeholder-wave">
                            <div className="placeholder col-4 mb-2" style={{height: '20px'}}></div>
                            <div className="placeholder col-12" style={{height: '44px', borderRadius: '4px'}}></div>
                        </div>
                    </div>
                </div>
                <div className="col text-center">
                    <div className="placeholder-wave">
                        <div className="placeholder col-12" style={{height: '44px', borderRadius: '4px'}}></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div></> : (
            <>
             <Formik
                onSubmit={submitForm}
                enableReinitialize
                initialValues={profileFormData}
                validationSchema={getValidationSchema(candidateType, ConfirmUnclaimedCandidateInvitationId)}
                initialErrors={initialErrors}
            >
                {(formikProps: FormikProps<any>) => {
                    const { handleSubmit, values, isSubmitting, setFieldTouched, setFieldValue } = formikProps;

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="card lt-shadow-sm mb-4">
                                {loadingSpin && (
                                    <div className="new-spinner p-absolute">
                                        <div>
                                            <span className="spinner-border spinner-border-sm custom-spinner-border" />
                                        </div>
                                        <p className="fs-14 custom-loading-text">Loading</p>
                                    </div>
                                )}
                                <div className="login-card-body">
                                    <div className="container-fluid">
                                        <div className="row mb-4">
                                            <div className="col-md-12">
                                                {history.location.pathname === '/api-integrate' ? (
                                                    <>
                                                        <h1 className="fs-28 fw-600 mb-2">Sign Up</h1>
                                                        <div className="fs-14 lt-text-secondary">
                                                            Already Have An Account?
                                                            <span
                                                                onClick={() => {
                                                                    setShowCustomSinUpTab?.(false);
                                                                }}
                                                                className="lt-text-primary-alt text-underline cursor-pointer"
                                                            >
                                                                {' '}
                                                                Login
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <h1 className="fs-32 fw-600 text-center mb-4">
                                                        Create Account and <br /> Try OfferX for free
                                                    </h1>
                                                )}
                                            </div>
                                        </div>

                                        {!showPasswordTab ? (
                                            <>
                                                <div className="row mb-5">
                                                    <div className="col-md-6">
                                                        <div className="mb-4">
                                                            <label className="fs-14 fw-400 mb-2" htmlFor="fname">
                                                                First Name {''}
                                                                <span className="lt-text-error">*</span>
                                                            </label>
                                                            <Field name="firstName">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);
                                                                    return (
                                                                        <>
                                                                            <input
                                                                                type="text"
                                                                                value={field.value}
                                                                                disabled={!showOTPBtn}
                                                                                onChange={(ev) => {
                                                                                    const regex = /^[a-zA-Z0-9 ]*$/;

                                                                                    if (regex.test(ev.target.value)) {
                                                                                        setFieldTouched(field.name);
                                                                                        setFieldValue(
                                                                                            field.name,
                                                                                            ev.target.value
                                                                                        );
                                                                                    }
                                                                                }}
                                                                                className={`form-control ${error ? 'is-invalid' : ''
                                                                                    }`}
                                                                                placeholder="Enter First Name"
                                                                                maxLength={100}
                                                                                id="fname"
                                                                            />
                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {error}
                                                                                </small>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-4">
                                                            <label className="fs-14 fw-400 mb-2" htmlFor="lname">
                                                                Last Name <span className="lt-text-error">*</span>
                                                            </label>
                                                            <Field name="lastName">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);
                                                                    return (
                                                                        <>
                                                                            <input
                                                                                type="text"
                                                                                value={field.value}
                                                                                disabled={!showOTPBtn}
                                                                                onChange={(ev) => {
                                                                                    const regex = /^[a-zA-Z0-9 ]*$/;

                                                                                    if (regex.test(ev.target.value)) {
                                                                                        setFieldTouched(field.name);
                                                                                        setFieldValue(
                                                                                            field.name,
                                                                                            ev.target.value
                                                                                        );
                                                                                    }
                                                                                }}
                                                                                className={`form-control ${error ? 'is-invalid' : ''
                                                                                    }`}
                                                                                placeholder="Enter Last Name"
                                                                                maxLength={100}
                                                                                id="lname"
                                                                            />
                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {error}
                                                                                </small>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-12 col-md-12">
                                                        <div className="mb-4">
                                                            <label className="fs-14 fw-400 mb-2" htmlFor="contact">
                                                                Contact Number <span className="lt-text-error">*</span>
                                                            </label>

                                                            <div className="d-flex">
                                                                <Field name="OwnerCountryCode">
                                                                    {(fieldProps: FieldProps) => {
                                                                        const { field, form } = fieldProps;
                                                                        const error =
                                                                            getValue(form.touched, field.name) &&
                                                                            getValue(form.errors, field.name);
                                                                        return (
                                                                            <>
                                                                                <div className="">
                                                                                    <PhoneCodeDropdown
                                                                                        id="companyUser"
                                                                                        companyUserValue={field.value}
                                                                                        setFieldValue={setFieldValue}
                                                                                        disabled={!showOTPBtn}
                                                                                    />
                                                                                </div>
                                                                            </>
                                                                        );
                                                                    }}
                                                                </Field>

                                                                <Field name="OwnerPhoneNumber">
                                                                    {(fieldProps: FieldProps) => {
                                                                        const { field, form } = fieldProps;
                                                                        const error =
                                                                            getValue(form.touched, field.name) &&
                                                                            getValue(form.errors, field.name);

                                                                        if (error) {
                                                                            setUserPhoneCodeError(error.toString());
                                                                        } else {
                                                                            setUserPhoneCodeError('');
                                                                        }
                                                                        return (
                                                                            <>
                                                                                <input
                                                                                    type="text"
                                                                                    value={field.value}
                                                                                    disabled={!showOTPBtn}
                                                                                    onChange={(ev) => {
                                                                                        const regex = /^\d+$/;

                                                                                        if (
                                                                                            regex.test(ev.target.value)
                                                                                        ) {
                                                                                            setFieldTouched(field.name);
                                                                                            setFieldValue(
                                                                                                field.name,
                                                                                                ev.target.value
                                                                                            );
                                                                                        } else if (
                                                                                            ev.target.value === ''
                                                                                        ) {
                                                                                            setFieldValue(
                                                                                                field.name,
                                                                                                ''
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                    className={`form-control form-phone-border ${error
                                                                                            ? 'phone-input is-invalid'
                                                                                            : ''
                                                                                        }`}
                                                                                    placeholder="Enter Contact Number"
                                                                                    maxLength={10}
                                                                                    id="contact"
                                                                                />
                                                                            </>
                                                                        );
                                                                    }}
                                                                </Field>
                                                            </div>
                                                            <small className="text-danger fs-12">
                                                                {userPhoneCodeError}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="col text-center">
                                                        <button
                                                            className="btn btn-primary w-100 h-44"
                                                            type="button"
                                                            disabled={
                                                                !values.firstName ||
                                                                !values.lastName ||
                                                                values.OwnerPhoneNumber.length !== 10
                                                            }
                                                            onClick={() => {
                                                                if (
                                                                    values.firstName &&
                                                                    values.lastName &&
                                                                    values.OwnerPhoneNumber
                                                                ) {
                                                                    // setShowOTPBtn?.(false);
                                                                    setIsOTPNotValidated(false);
                                                                    setShowPasswordTab?.(true);
                                                                    // setShowNextTab(true);
                                                                }
                                                            }}
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="row">
                                                    {isNewRegUnclaimedAcc && (
                                                        <div className="col-md-12 mb-2">
                                                            <label className="fs-14 fw-400 mb-2 " htmlFor="email">
                                                                Email <span className="lt-text-error">*</span>
                                                            </label>

                                                            <Field name="email">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);
                                                                    if (
                                                                        values.email.trim() !== '' &&
                                                                        values.firstName.trim() !== '' &&
                                                                        values.lastName.trim() !== '' &&
                                                                        values.OwnerPhoneNumber.length === 10 &&
                                                                        values.OwnerPhoneNumber.trim() !== '' &&
                                                                        !error
                                                                    ) {
                                                                        setEnableGetOtpBtn(false);
                                                                    } else {
                                                                        setEnableGetOtpBtn(true);
                                                                    }
                                                                    return (
                                                                        <>
                                                                            <div className="position-relative">
                                                                                <input
                                                                                    type="email"
                                                                                    onKeyDown={(event) => {
                                                                                        if (event.key === 'Enter') {
                                                                                            event.preventDefault();
                                                                                        }
                                                                                    }}
                                                                                    disabled={
                                                                                        !showOTPBtn ||
                                                                                            newRegCandidateEmail
                                                                                            ? true
                                                                                            : false
                                                                                    }
                                                                                    className="form-control"
                                                                                    value={values.email.trim()}
                                                                                    placeholder="Enter Email"
                                                                                    id="email"
                                                                                    onChange={(ev) => {
                                                                                        setFieldTouched(field.name);
                                                                                        setShowInvalidClaimedEmail(
                                                                                            false
                                                                                        );
                                                                                        setFieldValue(
                                                                                            field.name,
                                                                                            ev.target.value.trim()
                                                                                        );
                                                                                    }}
                                                                                />
                                                                                {otpVerified && (
                                                                                    <i className="bi bi-check-circle-fill lt-text-success email-check"></i>
                                                                                )}
                                                                            </div>

                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {showInvalidClaimedEmail
                                                                                        ? 'Please use correct email to search as per the hint!'
                                                                                        : error}
                                                                                </small>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                    )}
                                                    <div className="col-md-12">
                                                        <div className="mb-0">
                                                            <label className="fs-14 fw-400 mb-2" htmlFor="password">
                                                                Password <span className="lt-text-error">*</span>
                                                            </label>
                                                            <Field name="password">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);

                                                                    return (
                                                                        <div className="position-relative">
                                                                            <input
                                                                                type={`${showPass ? 'text' : 'password'
                                                                                    }`}
                                                                                value={field.value}
                                                                                onChange={(ev) => {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                }}
                                                                                className="form-control"
                                                                                placeholder="Enter Password"
                                                                                minLength={8}
                                                                                maxLength={16}
                                                                                id="password"
                                                                            />
                                                                            <i
                                                                                onClick={() => setShowPass(!showPass)}
                                                                                className={`bi ${showPass ? 'bi-eye' : 'bi-eye-slash'
                                                                                    } password-toggle cursor-pointer`}
                                                                            ></i>
                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {error}
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }}
                                                            </Field>
                                                            <div className="d-flex justify-content-start mt-2">
                                                                <ul className="fs-12 lt-text-disabled row">
                                                                    <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                        One uppercase character
                                                                    </li>
                                                                    <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                        One lowercase character
                                                                    </li>
                                                                    <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                        One special character
                                                                    </li>
                                                                    <li className="lt-text-disabled col-6 px-0 pb-1">
                                                                        One number
                                                                    </li>
                                                                    <li className="lt-text-disabled col-12 px-0 pb-1">
                                                                        Should be 8 - 16 characters
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <div className="mb-4">
                                                            <label className="fs-14 fw-400 mb-2" htmlFor="cpassword">
                                                                Confirm Password{' '}
                                                                <span className="lt-text-error">*</span>
                                                            </label>
                                                            <Field name="cpassword">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);

                                                                    return (
                                                                        <div className="position-relative">
                                                                            <input
                                                                                type={`${showCpass ? 'text' : 'password'
                                                                                    }`}
                                                                                value={field.value}
                                                                                onChange={(ev) => {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                }}
                                                                                className="form-control"
                                                                                placeholder="Confirm Password"
                                                                                minLength={8}
                                                                                id="cpassword"
                                                                            />
                                                                            <i
                                                                                onClick={() => setShowCpass(!showCpass)}
                                                                                className={`bi ${showCpass
                                                                                        ? 'bi-eye'
                                                                                        : 'bi-eye-slash'
                                                                                    } password-toggle cursor-pointer`}
                                                                            ></i>
                                                                            {typeof error === 'string' && (
                                                                                <small className="text-danger fs-12">
                                                                                    {error}
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <div className="text-end">
                                                            <button
                                                                type="submit"
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        event.preventDefault();
                                                                    }
                                                                }}
                                                                onClick={() => {
                                                                    convertToBase64(values.cpassword);

                                                                    sessionStorageSetItem(
                                                                        'user-basic-details',
                                                                        {
                                                                            FirstName: values.firstName,
                                                                            LastName: values.lastName,
                                                                            OwnerPhoneNumber: values.OwnerPhoneNumber,
                                                                            OwnerCountryCode: values.OwnerCountryCode,
                                                                            UserCountryCode: values.OwnerCountryCode,
                                                                            Email: customEmail || values.email,
                                                                            Country: country || "101",
                                                                        },
                                                                        false
                                                                    );
                                                                }}
                                                                className="btn btn-primary w-100 h-44 mb-4"
                                                                formNoValidate
                                                            >
                                                                Create Password
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Formik>
            </>
           )}
        </div>
    );
};

export default CustomAuth;

const getValidationSchema = (candidateType: string, ConfirmUnclaimedCandidateInvitationId: any) => {
    if (candidateType === 'candidate') {
        return objectYup().shape({
            firstName: stringYup(),
            lastName: stringYup(),
            OwnerPhoneNumber: ConfirmUnclaimedCandidateInvitationId ? stringYup() : stringYup()
                .min(10, 'Contact number must be at least 10 digits.'),
            email: ConfirmUnclaimedCandidateInvitationId
                ? stringYup()
                    .matches(emailRegexp, {
                        message: 'Invalid email format.',
                    })
                    .required('Email is required.')
                : stringYup().matches(emailRegexp, {
                    message: 'Invalid email format.',
                }),
            password: stringYup()
                .required('Password is required.')
                .min(8, 'Password must be in between 8 - 16 characters.')
                .matches(passwordRegExp, 'Password must match the below conditions.'),
            cpassword: stringYup()
                .nullable()
                .oneOf([refYup('password'), null], 'Password and Confirm password must match.')
                .required('Confirm Password is required.'),
        });
    } else {
        return objectYup().shape({
            firstName: stringYup().required('First name is required.'),
            lastName: stringYup().required('Last name is required.'),
            OwnerPhoneNumber: stringYup()
                .required('Contact number is required and must be 10 digit number.')
                .min(10, 'Contact number must be at least 10 digits.'),
            email: ConfirmUnclaimedCandidateInvitationId
                ? stringYup()
                    .matches(emailRegexp, {
                        message: 'Invalid email format.',
                    })
                    .required('Email is required.')
                : stringYup().matches(emailRegexp, {
                    message: 'Invalid email format.',
                }),
            password: stringYup()
                .required('Password is required.')
                .min(8, 'Password must be in between 8 - 16 characters.')
                .matches(passwordRegExp, 'Password must match the below conditions.'),
            cpassword: stringYup()
                .nullable()
                .oneOf([refYup('password'), null], 'Password and Confirm password must match.')
                .required('Confirm Password is required.'),
        });
    }
};
